export class Timeline<T> {
  private ticks: number[] = [];
  private values: T[] = [];

  private lastIndex: number = -1;
  private lastTick: number = -Infinity;

  constructor() {}

  set(tick: number, value: T): void {
    const index = this.binarySearch(tick);

    if (index < this.ticks.length && this.ticks[index] === tick) {
      this.values[index] = value;
    } else {
      this.ticks.splice(index, 0, tick);
      this.values.splice(index, 0, value);
    }

    if (index <= this.lastIndex) {
      this.lastIndex = -1;
      this.lastTick = -Infinity;
    }
  }

  getIndexAt(tick: number): number {
    if (this.ticks.length === 0) return -1;

    if (
      this.lastIndex >= 0 &&
      tick >= this.lastTick
    ) {
      while (
        this.lastIndex + 1 < this.ticks.length &&
        this.ticks[this.lastIndex + 1] <= tick
      ) {
        this.lastIndex++;
      }

      this.lastTick = tick;
      return this.lastIndex;
    }

    const index = this.binarySearch(tick);

    let resultIndex: number;

    if (index < this.ticks.length && this.ticks[index] === tick) {
      resultIndex = index;
    } else {
      resultIndex = index - 1;
    }

    if (resultIndex >= 0) {
      this.lastIndex = resultIndex;
      this.lastTick = tick;
    }

    return resultIndex;
  }

  getAt(tick: number): T | undefined {
    const index = this.getIndexAt(tick);
    return index >= 0 ? this.values[index] : undefined;
  }

  getByIndex(index: number): T | undefined {
    if (index < 0 || index >= this.values.length) return undefined;
    return this.values[index];
  }

  *entries(): IterableIterator<{ tick: number; value: T }> {
    for (let i = 0; i < this.ticks.length; i++) {
      yield { tick: this.ticks[i], value: this.values[i] };
    }
  }

  *range(from: number, to: number): IterableIterator<T> {
    if (this.ticks.length === 0) return;

    // Find first index where tick >= from
    let low = 0;
    let high = this.ticks.length;

    while (low < high) {
      const mid = (low + high) >> 1;
      if (this.ticks[mid] < from) {
        low = mid + 1;
      } else {
        high = mid;
      }
    }

    // Yield all values in range [from, to]
    for (let i = low; i < this.ticks.length && this.ticks[i] <= to; i++) {
      yield this.values[i];
    }
  }

  private binarySearch(tick: number): number {
    let low = 0;
    let high = this.ticks.length;

    while (low < high) {
      const mid = (low + high) >> 1;

      if (this.ticks[mid] <= tick) {
        low = mid + 1;
      } else {
        high = mid;
      }
    }

    return low;
  }
}
